import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiUser, FiLogOut, FiGrid, FiHeart, FiMoon, FiSun, FiGlobe, FiBell, FiMenu, FiX, FiCreditCard } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage, LANGUAGES } from '../../context/LanguageContext';
import SearchBar from './SearchBar';
import NotificationBell from './NotificationBell';
import RoleSwitcher from './RoleSwitcher';
import logoImg from '../../assets/logo-256.png';

// Reusable dropdown hook — closes on outside click
const useDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return { open, setOpen, ref };
};

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();
  const { isDark, toggleTheme } = useTheme();
  const { lang, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const langDrop = useDropdown();
  const userDrop = useDropdown();

  const handleLogout = async () => {
    userDrop.setOpen(false);
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    const roleRoutes = {
      super_admin: '/super-admin/dashboard',
      admin: '/admin/dashboard',
      farmer: '/farmer/dashboard',
      individual_buyer: '/account/orders',
      wholesale_buyer: '/account/orders',
      retailer: '/account/orders',
      delivery: '/delivery/dashboard',
      support: '/support/dashboard',
      affiliate: '/affiliate/dashboard',
      warehouse: '/warehouse/dashboard',
      moderator: '/moderator/dashboard',
      finance: '/finance/dashboard',
      farmer_staff: '/farmer/dashboard',
      returns_manager: '/returns-manager/dashboard',
      marketing: '/marketing/dashboard',
      catalog_manager: '/catalog/dashboard',
      farmer_onboarding: '/farmer-onboarding/dashboard',
      quality_inspector: '/quality/dashboard',
      community_manager: '/community/dashboard',
      dispute_arbitrator: '/disputes/dashboard',
      qa_tester: '/qa-tester/dashboard',
      regional_manager: '/regional/dashboard',
    };
    return roleRoutes[user.role] || '/account/orders';
  };

  return (
    <nav className="navbar navbar-expand-lg fd-navbar sticky-top">
      <div className="container">
        <Link className="navbar-brand" to="/" style={{ padding: '2px 0' }}>
          <img src={logoImg} alt="FarmDirectly" style={{ height: '52px', width: 'auto' }} />
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
        </button>

        <div className={`collapse navbar-collapse ${mobileOpen ? 'show' : ''}`} id="mainNav">
          <ul className="navbar-nav me-auto">
            <li className="nav-item">
              <NavLink className="nav-link" to="/products" onClick={() => setMobileOpen(false)}>🛒 {t('products')}</NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/flash-deals" onClick={() => setMobileOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <span style={{ color: '#EAB308', fontSize: '12px' }}>⚡</span> {t('deals')}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/stores" onClick={() => setMobileOpen(false)}>🌾 Farms</NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/categories" onClick={() => setMobileOpen(false)}>📋 Categories</NavLink>
            </li>
          </ul>

          <ul className="navbar-nav align-items-center">
            {/* Search */}
            <li className="nav-item me-3 d-none d-lg-block">
              <SearchBar />
            </li>

            {/* Dark Mode Toggle */}
            <li className="nav-item me-2">
              <button
                className="nav-link btn btn-link p-0 border-0"
                onClick={toggleTheme}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <FiSun size={18} /> : <FiMoon size={18} />}
              </button>
            </li>

            {/* Language Switcher — React-controlled */}
            <li className="nav-item dropdown me-2" ref={langDrop.ref}>
              <button
                className="nav-link btn btn-link p-0 border-0 dropdown-toggle"
                onClick={() => langDrop.setOpen(!langDrop.open)}
                title="Language"
              >
                <FiGlobe size={18} />
              </button>
              {langDrop.open && (
                <ul className="dropdown-menu dropdown-menu-end show" style={{ minWidth: '120px' }}>
                  {LANGUAGES.map((l) => (
                    <li key={l.code}>
                      <button
                        className={`dropdown-item small ${lang === l.code ? 'fw-bold' : ''}`}
                        onClick={() => { setLanguage(l.code); langDrop.setOpen(false); }}
                      >
                        {l.flag} {l.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* Notification Bell (authenticated only) */}
            {isAuthenticated && <NotificationBell />}

            {/* Role Switcher */}
            {isAuthenticated && <li className="nav-item me-2"><RoleSwitcher /></li>}

            {/* Wishlist */}
            {isAuthenticated && (
              <li className="nav-item me-2">
                <Link to="/account/wishlist" className="nav-link" onClick={() => setMobileOpen(false)}>
                  <FiHeart size={20} />
                </Link>
              </li>
            )}

            {/* Cart */}
            <li className="nav-item me-3">
              <Link to="/cart" className="nav-link position-relative" onClick={() => setMobileOpen(false)}>
                <FiShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="cart-badge">{itemCount}</span>
                )}
              </Link>
            </li>

            {isAuthenticated ? (
              <li className="nav-item dropdown" ref={userDrop.ref}>
                <button
                  className="nav-link dropdown-toggle btn btn-link"
                  onClick={() => userDrop.setOpen(!userDrop.open)}
                >
                  <FiUser size={18} className="me-1" />
                  {user?.firstName}
                </button>
                {userDrop.open && (
                  <ul className="dropdown-menu dropdown-menu-end show">
                    <li>
                      <Link className="dropdown-item" to={getDashboardLink()} onClick={() => userDrop.setOpen(false)}>
                        <FiGrid className="me-2" /> Dashboard
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/account/orders" onClick={() => userDrop.setOpen(false)}>
                        <FiGrid className="me-2" /> My Orders
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/account/profile" onClick={() => userDrop.setOpen(false)}>
                        <FiUser className="me-2" /> Profile
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/account/wishlist" onClick={() => userDrop.setOpen(false)}>
                        <FiHeart className="me-2" /> Wishlist
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/account/wallet" onClick={() => userDrop.setOpen(false)}>
                        <FiCreditCard className="me-2" /> Wallet
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/account/notifications" onClick={() => userDrop.setOpen(false)}>
                        <FiBell className="me-2" /> Notification Settings
                      </Link>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item" onClick={handleLogout}>
                        <FiLogOut className="me-2" /> Logout
                      </button>
                    </li>
                  </ul>
                )}
              </li>
            ) : (
              <>
                <li className="nav-item">
                  <Link to="/login" className="btn btn-fd-outline btn-sm me-2" onClick={() => setMobileOpen(false)}>
                    {t('login')}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/register" className="btn btn-fd-primary btn-sm" onClick={() => setMobileOpen(false)}>
                    {t('signUp')}
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
